import { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';

import styles from './ExportChapterPdfDialog.module.css';

interface BuildChapterPdfPrintUrlOptions {
  componentId: string;
  courseId: string;
  includeQuestions: boolean;
  includeVideoUrls: boolean;
}

interface ExportChapterPdfDialogProps {
  chapterTitle: string;
  componentId: string;
  courseTitle: string;
  courseId: string;
  hasQuestions: boolean;
  hasReading: boolean;
  hasVideo: boolean;
  onOpenChange(open: boolean): void;
  open: boolean;
}

export function buildChapterPdfPrintUrl({
  componentId,
  courseId,
  includeQuestions,
  includeVideoUrls,
}: BuildChapterPdfPrintUrlOptions) {
  const params = new URLSearchParams({
    questions: includeQuestions ? '1' : '0',
    videos: includeVideoUrls ? '1' : '0',
  });

  return `/app/courses/${encodeURIComponent(courseId)}/${encodeURIComponent(
    componentId,
  )}/print?${params.toString()}`;
}

export function ExportChapterPdfDialog({
  chapterTitle,
  componentId,
  courseId,
  courseTitle,
  hasQuestions,
  hasReading,
  hasVideo,
  onOpenChange,
  open,
}: ExportChapterPdfDialogProps) {
  const [includeVideoUrls, setIncludeVideoUrls] = useState(hasVideo);
  const [includeQuestions, setIncludeQuestions] = useState(hasQuestions);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setIncludeVideoUrls(hasVideo);
    setIncludeQuestions(hasQuestions);
    setError(null);
  }, [hasQuestions, hasVideo, open]);

  const handleExport = useCallback(() => {
    const printUrl = buildChapterPdfPrintUrl({
      componentId,
      courseId,
      includeQuestions: hasQuestions ? includeQuestions : false,
      includeVideoUrls: hasVideo ? includeVideoUrls : false,
    });
    const printWindow = window.open(printUrl, '_blank');

    if (!printWindow) {
      setError(
        'Your browser blocked the print window. Allow pop-ups for Kudu, then try Export again.',
      );
      return;
    }

    printWindow.opener = null;
    onOpenChange(false);
  }, [
    componentId,
    courseId,
    hasQuestions,
    hasVideo,
    includeQuestions,
    includeVideoUrls,
    onOpenChange,
  ]);

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.header}>
            <div>
              <Dialog.Title className={styles.title}>
                Export chapter to PDF
              </Dialog.Title>
              <Dialog.Description asChild>
                <p className={styles.description}>
                  Choose what to include, then use your browser's print dialog
                  to save the chapter as a PDF.
                </p>
              </Dialog.Description>
            </div>
          </div>

          <div className={styles.body}>
            <section aria-label="Chapter details" className={styles.chapterSummary}>
              <span className={styles.courseTitle}>{courseTitle}</span>
              <span className={styles.chapterTitle}>{chapterTitle}</span>
            </section>

            {hasVideo || hasQuestions ? (
              <div className={styles.optionList}>
                {hasVideo ? (
                  <label className={styles.option}>
                    <input
                      checked={includeVideoUrls}
                      className={styles.checkbox}
                      onChange={(event) =>
                        setIncludeVideoUrls(event.target.checked)
                      }
                      type="checkbox"
                    />
                    <span>Include YouTube video URL(s)</span>
                  </label>
                ) : null}

                {hasQuestions ? (
                  <label className={styles.option}>
                    <input
                      checked={includeQuestions}
                      className={styles.checkbox}
                      onChange={(event) =>
                        setIncludeQuestions(event.target.checked)
                      }
                      type="checkbox"
                    />
                    <span>
                      Include homework and pre-lecture questions. Solutions are
                      never included.
                    </span>
                  </label>
                ) : null}
              </div>
            ) : (
              <p className={styles.helperText}>
                {hasReading
                  ? 'This export will include the central reading content only.'
                  : 'Nothing in this chapter is available yet, so the export would hold only its title.'}
              </p>
            )}

            {error ? (
              <p className={styles.error} role="alert">
                {error}
              </p>
            ) : null}
          </div>

          <div className={styles.footer}>
            <Button onClick={() => onOpenChange(false)} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleExport}>Export</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
